<template>
  <div
    class="t-typeahead"
    ref="typeaheadElement"
    :class="{'t-typeahead--disabled': disabled}"
  >
    <div class="t-typeahead__container">
      <span
        v-if="initialLoadPending"
        class="t-loading"
      />
      <t-tag-list v-if="multiple" inline>
        <t-tag
          class="t-tag"
          v-for="option in selected"
          :key="option.id"
          type="plain"
          @click.native="deselect(option)"
          closeable
          small
        >
          {{ getOptionText(option) }}
        </t-tag>
      </t-tag-list>
      <t-tag
        v-else-if="selected.length !== 0"
        type="plain"
        @click.native="deselect(selected[0])"
        small
        closeable
      >{{ getOptionText(selected[0]) }}</t-tag>
      <t-input
        :disabled="disabled"
        :placeholder="placeholder"
        class="t-typeahead__search"
        v-model="queryString"
        @keydown.delete="onInputDelete"
        @input="onQueryUpdate"
        autocomplete="off"
      />
    </div>

    <ul
      v-show="shouldShowOptions"
      class="t-typeahead__options"
    >
      <li
        v-if="!hasReachedQueryThreshold"
        class="t-typeahead__option"
      >{{ minQueryWarning }}</li>
      <li
        v-else-if="loading"
        class="t-typeahead__option"
      ><span class="t-loading t-loading--centered" /></li>
      <li
        v-else-if="options.length === 0"
        class="t-typeahead__option"
      >{{ noResultsMessage }}</li>
      <li
        class="t-typeahead__option"
        :class="{
          't-typeahead__option--selected': isSelected(option)
        }"
        v-else
        v-for="option in options"
        :key="getOptionValue(option)"
        @click="onClickOption(option)"
      >
        <slot name="option" :option="option">
          {{ getOptionText(option) }}
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
import { onClickOutside } from '@vueuse/core';
import { debounce } from '../../utils';

export default {
  name: 't-typeahead',

  props: {
    value: {
      type: [String, Array, Number],
      required: false,
      default: null,
    },

    query: {
      type: Function,
      rquired: true,
      default: () => {},
    },

    initialQuery: {
      type: Function,
      rquired: true,
      default: () => ([]),
    },

    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },

    minQueryStringLength: {
      type: Number,
      required: false,
      default: 3,
    },

    optionValue: {
      type: [String, Function],
      required: false,
      default: 'id',
    },

    optionText: {
      type: [String, Function],
      required: false,
      default: 'label',
    },

    parseQueryResponse: {
      type: Function,
      required: false,
      default: (response) => response,
    },

    minQueryWarningMessage: {
      type: String,
      required: false,
      default: null,
    },

    noResultsMessage: {
      type: String,
      required: false,
      default: 'No results found.',
    },

    placeholder: {
      type: String,
      required: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      options: [],
      selected: [],
      loading: false,
      initialLoadPending: true,
      queryString: '',
      shouldShowOptions: false,
    };
  },

  async created() {
    this.initialLoadPending = true;
    if (this.value?.length) {
      const result = await this.initialQuery(this.value);
      this.selected = this.parseQueryResponse(result);
    }
    this.initialLoadPending = false;
  },

  mounted() {
    onClickOutside(this.$refs.typeaheadElement, () => {
      this.shouldShowOptions = false;
    });
  },

  computed: {
    hasReachedQueryThreshold() {
      return this.queryString?.length >= this.minQueryStringLength;
    },

    minQueryWarning() {
      return this.minQueryWarningMessage
        || `Please type at least ${this.minQueryStringLength} characters.`;
    },
  },

  watch: {
    selected(value) {
      if (value?.length === 0) {
        return this.handleEmptyEmit();
      }

      if (this.multiple) {
        return this.handleMultipleEmit(value);
      }

      return this.handleSingleEmit(value);
    },
  },

  methods: {
    getOptionText(option) {
      return typeof (this.optionText) === 'string'
        ? option[this.optionText]
        : this.optionText(option);
    },

    onInputDelete() {
      if (this.queryString || !this.value?.length) return;
      this.deselect(this.selected[this.selected.length - 1]);
    },

    getOptionValue(option) {
      return typeof (this.optionValue) === 'string'
        ? option[this.optionValue]
        : this.optionValue(option);
    },

    isSelected(option) {
      return this.selected.some(
        (selectedOption) => this.getOptionValue(selectedOption) === this.getOptionValue(option),
      );
    },

    deselect(option) {
      this.selected = this.selected.filter(
        (selectedOption) => !(this.getOptionValue(option) === this.getOptionValue(selectedOption)),
      );
      this.shouldShowOptions = false;
    },

    select(option) {
      if (this.multiple) {
        this.selected.push(option);
      } else {
        this.selected = [option];
      }

      this.queryString = null;
      this.shouldShowOptions = false;
    },

    handleMultipleEmit(value) {
      this.$emit('input', value?.map((option) => this.getOptionValue(option)));
      this.$emit('change', value?.map((option) => this.getOptionValue(option)));
    },

    handleSingleEmit(value) {
      this.$emit('input', this.getOptionValue(value[0]));
      this.$emit('change', this.getOptionValue(value[0]));
    },

    handleEmptyEmit() {
      this.$emit('input', null);
      this.$emit('change', null);
    },

    onClickOption(option) {
      return this.isSelected(option)
        ? this.deselect(option)
        : this.select(option);
    },

    async onQueryUpdate() {
      this.shouldShowOptions = true;

      if (this.hasReachedQueryThreshold) {
        this.loading = true;
        this.options = [];
        await this.search();
      }
    },

    search: debounce(async function toDebounce() {
      await this.fetchOptions(this.queryString);
      this.loading = false;
    }, 200),

    async fetchOptions(queryString) {
      const response = await this.query(queryString);
      this.options = this.parseQueryResponse(response);
    },
  },
};
</script>
